// Preview-first pick-per-cluster UI for merging duplicate lead cards.
// Loads the dry-run plan, lets the admin pick a survivor (or skip) per cluster,
// then applies. Nothing is written until "Apply merges".
import React, { useEffect, useState } from "react";
import { mergeDuplicateLeads } from "../../utils/firebasefunctions";
import { useActionDialog } from "../ActionDialog";

const field = (l, k, fallback = "—") => (l && l[k] ? String(l[k]) : fallback);

export default function DuplicateLeadsModal({ onClose }) {
  const { confirm, notify, dialog } = useActionDialog();
  const [loading, setLoading] = useState(true);
  const [clusters, setClusters] = useState([]);
  const [choices, setChoices] = useState({}); // key -> survivorId | "skip"
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const plan = await mergeDuplicateLeads({ dryRun: true });
        setClusters(plan.clusters || []);
        const init = {};
        (plan.clusters || []).forEach((c) => (init[c.key] = c.suggestedSurvivorId));
        setChoices(init);
      } catch (e) {
        console.log("Duplicate scan failed", e);
        // Wait for the reader to dismiss before pulling the modal away, or the
        // message vanishes with it.
        await notify({ title: "Scan failed", body: "See the browser console for details." });
        onClose();
      } finally {
        setLoading(false);
      }
    })();
    // notify is stable across renders; re-running this scan on it would refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const apply = async () => {
    const active = Object.values(choices).filter((v) => v && v !== "skip").length;
    if (active === 0) {
      await notify({
        title: "Nothing to merge",
        body: "Every cluster is set to Skip.",
      });
      return;
    }
    const ok = await confirm({
      title: "Merge duplicate leads",
      body: `Merge ${active} cluster(s)?`,
      note: "Non-survivor cards are archived, which is reversible.",
      confirmLabel: "Apply merges",
    });
    if (!ok) return;
    setApplying(true);
    try {
      const res = await mergeDuplicateLeads({ choices });
      await notify({
        title: "Merge complete",
        body:
          `Merged ${res.mergedClusters} cluster(s).` +
          (res.failed ? ` ${res.failed} write(s) FAILED, re-run to retry.` : ""),
      });
      onClose(true);
    } catch (e) {
      console.log("Merge failed", e);
      await notify({ title: "Merge failed", body: "See the browser console for details." });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-[#1E293B]">Merge Duplicate Leads</h3>
          <button onClick={() => onClose(false)} className="text-gray-500 hover:text-gray-800">✕</button>
        </div>

        {loading && <p>Scanning pipelines…</p>}
        {!loading && clusters.length === 0 && <p>No duplicate leads found. 🎉</p>}

        {!loading &&
          clusters.map((c) => (
            <div key={c.key} className="border border-gray-200 rounded-lg p-3 mb-4">
              <div className="text-xs font-semibold uppercase text-gray-500 mb-2">
                {c.pipeline} pipeline — {c.leads.length} copies
              </div>
              <div className="space-y-2">
                {c.leads.map((l) => (
                  <label key={l.id} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name={c.key}
                      checked={choices[c.key] === l.id}
                      onChange={() => setChoices((p) => ({ ...p, [c.key]: l.id }))}
                      className="mt-1"
                    />
                    <span>
                      <b>{field(l, "contactName", "(no name)")}</b>
                      {c.pipeline === "listing" && <> — {field(l, "propertyAddress")}</>}
                      {" · "}stage: {field(l, "stage")} · agent: {field(l, "assignedAgentName", field(l, "agentId"))}
                      {" · "}source: {field(l, "leadSource", field(l, "source"))}
                      {l.id === c.suggestedSurvivorId ? "  ⭐ suggested" : ""}
                    </span>
                  </label>
                ))}
                <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
                  <input
                    type="radio"
                    name={c.key}
                    checked={choices[c.key] === "skip"}
                    onChange={() => setChoices((p) => ({ ...p, [c.key]: "skip" }))}
                  />
                  Skip this cluster (leave untouched)
                </label>
              </div>
            </div>
          ))}

        {!loading && clusters.length > 0 && (
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => onClose(false)} className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-semibold">
              Cancel
            </button>
            <button
              onClick={apply}
              disabled={applying}
              className="px-4 py-2 rounded-lg bg-[#1E293B] hover:bg-green-600 text-white text-sm font-semibold disabled:opacity-50"
            >
              {applying ? "Merging…" : "Apply merges"}
            </button>
          </div>
        )}
      </div>
      {dialog}
    </div>
  );
}
